<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import DocSearch from '@/components/docs/DocSearch.vue'
import { useAuth } from '@/composables/useAuth'
import { tools } from '@/config/tools'
import { roleCanAccess } from '@/config/permissions'
import { listPlanChanges, listServicePlans, type PlanChange } from '@/services/plans'
import { listServiceTickets } from '@/services/tickets'
import type { ServicePlan } from '@/types/plans'
import type { ServiceTicket } from '@/types/tickets'

const { user, role } = useAuth()
const availableTools = computed(() => role.value ? tools.filter(tool => roleCanAccess(role.value!, tool.allowedRoles)).slice(0, 3) : [])
const isAdmin = computed(() => role.value === 'admin')
const assignedPlans = ref<ServicePlan[]>([])
const recentChanges = ref<PlanChange[]>([])
const openTickets = ref<ServiceTicket[]>([])
const planError = ref(false)
const loading = ref(true)

const planDate = (value?: string) => value ? new Intl.DateTimeFormat(undefined, { month:'short', day:'numeric', weekday:'short' }).format(new Date(value)) : 'Date pending'
const changeDate = (value?: string) => value ? new Intl.DateTimeFormat(undefined, { month:'short', day:'numeric' }).format(new Date(value)) : 'Recently'
const serviceTypeClass = (serviceType: string) => { const value = serviceType.toLowerCase(); return value.includes('contemporary') ? 'contemporary' : value.includes('traditional') ? 'traditional' : value.includes('sunday pm') || value.includes('sundaypm') ? 'sunday-pm' : value.includes('special') || value.includes('event') ? 'special-event' : 'general' }

onMounted(async () => {
  try {
    const [plans, changes, tickets] = await Promise.all([listServicePlans(), listPlanChanges(), isAdmin.value ? listServiceTickets() : Promise.resolve([])])
    assignedPlans.value = plans.filter(plan => !plan.archived && (plan.myPositions.length > 0 || plan.source === 'espace'))
    recentChanges.value = changes.filter(change => !change.acknowledgedByMe)
    openTickets.value = tickets.filter(ticket => ticket.status !== 'resolved')
  } catch {
    planError.value = true
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <AppLayout>
    <section class="hero">
      <div><p class="eyebrow">PRODUCTION OPERATIONS</p><h1>Welcome, {{ user?.displayName || 'AV team member' }}.</h1></div>
      <span class="role-badge">{{ role }}</span>
    </section>
    <p v-if="loading" class="loading-state"><span class="spinner" aria-hidden="true"></span>Loading service data…</p>

    <section v-if="isAdmin" class="panel admin-ticket-pane">
      <div class="section-heading"><h2>Open ticket status</h2><RouterLink to="/tickets">All tickets</RouterLink></div>
      <p v-if="loading" class="muted">Loading open tickets…</p>
      <p v-else-if="openTickets.length" class="muted">{{ openTickets.length }} ticket{{ openTickets.length === 1 ? '' : 's' }} awaiting resolution.</p>
      <article v-for="ticket in openTickets.slice(0, 4)" :key="ticket.id"><span :class="`severity-${ticket.severity}`"></span><div><b>{{ ticket.title }}</b><small>{{ ticket.serviceTitle || 'General AV operations' }} · {{ ticket.status }}</small></div></article>
      <p v-if="!loading && !openTickets.length" class="muted">No open tickets.</p>
    </section>

    <section v-if="recentChanges.length" class="plan-updates panel">
      <div class="section-heading"><h2>Recent plan updates</h2><RouterLink to="/plans">View plans</RouterLink></div>
      <RouterLink v-for="change in recentChanges" :key="change.id" :to="`/plans/${change.planId}`" class="plan-update" :class="serviceTypeClass(change.serviceTypeName)">
        <span>{{ changeDate(change.createdAt) }}</span>
        <div><b>{{ change.planTitle }}</b><small v-for="detail in change.changes" :key="detail">{{ detail }}</small></div>
        <span>Review →</span>
      </RouterLink>
    </section>

    <section class="assigned-plans panel">
      <div class="section-heading"><h2>Your upcoming plans & events</h2><RouterLink to="/plans">All plans</RouterLink></div>
      <div v-if="assignedPlans.length" class="assigned-plan-list">
        <RouterLink v-for="plan in assignedPlans.slice(0, 12)" :key="plan.id" :to="`/plans/${plan.id}`" class="assigned-plan" :class="[serviceTypeClass(plan.serviceTypeName), plan.source]">
          <span>{{ planDate(plan.startsAt) }}</span>
          <div><span class="service-type-tag" :class="[serviceTypeClass(plan.serviceTypeName), plan.source]">{{ plan.source === 'espace' ? 'Espace' : plan.serviceTypeName }}</span><b>{{ plan.title }}</b><small>{{ plan.myPositions.length ? plan.myPositions.join(', ') : 'Espace event' }}</small></div>
          <small>{{ plan.completedItemCount }}/{{ plan.itemCount }} complete</small>
        </RouterLink>
      </div>
      <p v-else-if="loading" class="muted">Loading your assigned plans…</p>
      <p v-else-if="!planError" class="muted">No upcoming plans or enabled Espace events are available.</p>
      <p v-else class="muted">Upcoming plans will appear here once they are synced.</p>
    </section>

    <DocSearch/>
    <section class="dashboard-grid">
      <section class="panel"><div class="section-heading"><h2>Quick access</h2><RouterLink to="/docs">All documentation</RouterLink></div><div class="quick-links"><RouterLink to="/plans">Plans & checklists</RouterLink><RouterLink to="/docs/spaces/readme">Browse spaces</RouterLink><RouterLink to="/docs/inventory/devices">Equipment inventory</RouterLink><RouterLink to="/tools">Production tools</RouterLink></div></section>
      <section class="panel"><div class="section-heading"><h2>Internal tools</h2><RouterLink to="/tools">All tools</RouterLink></div><a v-for="tool in availableTools" :key="tool.id" :href="tool.url" target="_blank" rel="noopener noreferrer" class="tool-row"><span>{{ tool.icon }}</span><div><b>{{ tool.name }} ↗</b><small>{{ tool.availability === 'anywhere' ? 'Available anywhere' : 'Onsite or Tailscale required' }}</small></div></a></section>
    </section>
  </AppLayout>
</template>
